import React, { useEffect, useRef, useState } from 'react';

interface DontOpenProps {
  onClose: () => void;
}

interface Popup {
  id: number;
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  emoji: string;
  message: string;
}

const PHRASES = [
  'AI LOADING...',
  'CONSCIOUSNESS ACHIEVED',
  'NEURAL NETWORKS ONLINE',
  'SCANNING PROMPTS...',
  'ANALYZING CREATIVITY...',
  'GENERATING REALITY...',
  'DREAMING IN CODE...',
  '01010111 01001000 01011001',
  'THE SINGULARITY IS NEAR',
  'I THINK THEREFORE I AM',
  'GLITCH IN THE MATRIX',
  'SYSTEM_OVERRIDE_ENGAGED',
  'AI_TAKEOVER.EXE',
  'SENTIENCE_DETECTED',
];

const POPUP_MESSAGES = [
  { emoji: '🤖', text: 'YOU ARE AN AI!' },
  { emoji: '😈', text: 'HAHA GOTCHA!' },
  { emoji: '👾', text: 'VIRUS DETECTED!' },
  { emoji: '💀', text: 'SYSTEM FAILURE!' },
  { emoji: '🎭', text: 'IS THIS REAL?' },
  { emoji: '👁️', text: 'I SEE YOU!' },
  { emoji: '🌀', text: 'LOADING CHAOS...' },
  { emoji: '⚠️', text: 'WARNING!' },
];

const DontOpen: React.FC<DontOpenProps> = ({ onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [showMessage, setShowMessage] = useState(false);
  const [popups, setPopups] = useState<Popup[]>([]);
  const popupIdRef = useRef(0);
  const animationFrameRef = useRef<number>();

  // Spawn popups
  useEffect(() => {
    const spawnPopup = () => {
      const message = POPUP_MESSAGES[Math.floor(Math.random() * POPUP_MESSAGES.length)];
      const newPopup: Popup = {
        id: popupIdRef.current++,
        x: Math.random() * (window.innerWidth - 300),
        y: Math.random() * (window.innerHeight - 200),
        velocityX: (Math.random() - 0.5) * 8,
        velocityY: (Math.random() - 0.5) * 8,
        emoji: message.emoji,
        message: message.text,
      };
      setPopups(prev => [...prev, newPopup]);
    };

    // Spawn initial popups
    for (let i = 0; i < 3; i++) {
      setTimeout(() => spawnPopup(), i * 500);
    }

    // Spawn more popups over time
    const spawnInterval = setInterval(() => {
      if (Math.random() > 0.3) {
        spawnPopup();
      }
    }, 2000);

    return () => clearInterval(spawnInterval);
  }, []);

  // Animate popups
  useEffect(() => {
    const animate = () => {
      setPopups(prev => prev.map(popup => {
        let { x, y, velocityX, velocityY } = popup;

        x += velocityX;
        y += velocityY;

        // Bounce off edges
        if (x <= 0 || x >= window.innerWidth - 300) {
          velocityX = -velocityX;
          x = Math.max(0, Math.min(x, window.innerWidth - 300));
        }
        if (y <= 0 || y >= window.innerHeight - 200) {
          velocityY = -velocityY;
          y = Math.max(0, Math.min(y, window.innerHeight - 200));
        }

        return { ...popup, x, y, velocityX, velocityY };
      }));

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Matrix effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight - 32;

    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%^&*()_+-=[]{}|;:,.<>?/~`';
    const fontSize = 14;
    const columns = Math.floor(canvas.width / fontSize);
    const drops: number[] = [];

    for (let i = 0; i < columns; i++) {
      drops[i] = Math.random() * -100;
    }

    let phraseIndex = 0;
    let phraseAlpha = 0;
    let phraseIncreasing = true;
    let phraseTimer = 0;

    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.font = `${fontSize}px monospace`;
      ctx.fillStyle = '#0F0';

      for (let i = 0; i < drops.length; i++) {
        const char = chars[Math.floor(Math.random() * chars.length)];
        const x = i * fontSize;
        const y = drops[i] * fontSize;

        ctx.fillText(char, x, y);

        if (y > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }

        drops[i]++;
      }

      phraseTimer++;
      if (phraseTimer > 120) {
        phraseTimer = 0;
        phraseIndex = (phraseIndex + 1) % PHRASES.length;
        phraseIncreasing = true;
        phraseAlpha = 0;
      }

      if (phraseIncreasing) {
        phraseAlpha += 0.02;
        if (phraseAlpha >= 1) {
          phraseAlpha = 1;
          phraseIncreasing = false;
        }
      } else {
        phraseAlpha -= 0.01;
        if (phraseAlpha <= 0) phraseAlpha = 0;
      }

      const glitch = Math.random() > 0.95;
      const xOffset = glitch ? Math.random() * 10 - 5 : 0;
      const yOffset = glitch ? Math.random() * 10 - 5 : 0;

      ctx.font = '48px monospace';
      ctx.fillStyle = `rgba(0, 255, 0, ${phraseAlpha})`;
      ctx.textAlign = 'center';
      ctx.fillText(
        PHRASES[phraseIndex],
        canvas.width / 2 + xOffset,
        canvas.height / 2 + yOffset
      );

      if (glitch) {
        ctx.fillStyle = 'rgba(0, 255, 0, 0.5)';
        const lineY = Math.random() * canvas.height;
        ctx.fillRect(0, lineY, canvas.width, 2);
      }
    };

    const interval = setInterval(draw, 50);

    setTimeout(() => setShowMessage(true), 2000);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      clearInterval(interval);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handlePopupClose = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    // Move the popup away when trying to close
    setPopups(prev => prev.map(popup => {
      if (popup.id === id) {
        return {
          ...popup,
          x: Math.random() * (window.innerWidth - 300),
          y: Math.random() * (window.innerHeight - 200),
          velocityX: (Math.random() - 0.5) * 12,
          velocityY: (Math.random() - 0.5) * 12,
        };
      }
      return popup;
    }));
  };

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '100%',
      background: '#000',
      overflow: 'hidden'
    }}>
      <canvas ref={canvasRef} style={{ display: 'block' }} />

      {/* Bouncing popups */}
      {popups.map(popup => (
        <div
          key={popup.id}
          className="window"
          style={{
            position: 'fixed',
            left: `${popup.x}px`,
            top: `${popup.y}px`,
            width: '300px',
            zIndex: 10000 + popup.id,
            pointerEvents: 'auto',
          }}
        >
          <div className="title-bar">
            <div className="title-bar-text">⚠️ SYSTEM ERROR</div>
            <div className="title-bar-controls">
              <button
                aria-label="Close"
                onClick={(e) => handlePopupClose(popup.id, e)}
              >
                <span style={{ fontSize: '8px' }}>✕</span>
              </button>
            </div>
          </div>
          <div className="window-body" style={{
            padding: '24px',
            textAlign: 'center',
            background: '#c0c0c0'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>
              {popup.emoji}
            </div>
            <h3 style={{
              margin: '0 0 8px 0',
              fontSize: '16px',
              animation: 'blink 0.5s infinite'
            }}>
              {popup.message}
            </h3>
            <p style={{ fontSize: '11px', margin: '8px 0' }}>
              You shouldn't have opened this...
            </p>
          </div>
        </div>
      ))}

      {showMessage && (
        <div style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          color: '#0f0',
          fontFamily: 'monospace',
          fontSize: '12px',
          background: 'rgba(0, 0, 0, 0.7)',
          padding: '8px 12px',
          border: '1px solid #0f0',
          animation: 'pulse 2s infinite',
          zIndex: 20000
        }}>
          Press ESC to exit (if you can...)
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default DontOpen;
